import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { playChime } from "../lib/chime";
import { FlowerOfLife, ChokuRei } from "../components/SacredGeometry";
import { Check, X, ArrowLeft, RotateCcw, Award } from "lucide-react";

export default function Quiz() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}/quiz`).then((r) => { setQuiz(r.data); setAnswers({}); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [courseId]);

  const submit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) {
      toast.error("Reflect upon every question before offering your answers.");
      return;
    }
    setSubmitting(true);
    try {
      const ordered = quiz.questions.map((_, i) => answers[i]);
      const { data } = await api.post(`/courses/${courseId}/quiz/submit`, { answers: ordered });
      setResult(data);
      if (data.passed) playChime();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => { setResult(null); setAnswers({}); };

  if (error) {
    return (
      <div className="min-h-screen pt-32 grid place-items-center bg-sanctuary-deep">
        <div className="text-center max-w-md px-6">
          <h1 className="font-display text-3xl text-parchment mb-3">A gate stands here</h1>
          <p className="text-text-muted mb-8">{error}</p>
          <Link to="/membership" className="px-7 py-3 rounded-full bg-gold text-sanctuary-deep font-medium">Become a member</Link>
        </div>
      </div>
    );
  }

  if (!quiz) return <div className="min-h-screen pt-32 text-center text-text-muted">Preparing the ceremony…</div>;

  if (result) {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-sanctuary-deep relative">
        <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] aura breathe pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <FlowerOfLife className="w-20 mx-auto text-gold/50 mb-6 floaty" />
          <div className="eyebrow mb-3">{result.passed ? "The ceremony is complete" : "Return and reflect"}</div>
          <h1 className="font-display text-5xl text-parchment font-light mb-4" data-testid="quiz-result-score">{result.score}%</h1>
          <p className="text-text-muted mb-10">
            {result.passed
              ? "You have attuned to this degree with clarity and grace."
              : "Not quite yet — sit with the teachings once more, then return. The path is patient."}
          </p>

          <div className="space-y-3 text-left mb-10">
            {result.feedback.map((f, i) => (
              <div key={i} className={`rounded-xl p-5 border ${f.correct ? "bg-sage/10 border-sage/30" : "bg-red-500/5 border-red-500/20"}`} data-testid={`quiz-feedback-${i}`}>
                <div className="flex items-start gap-3">
                  <span className={`w-6 h-6 rounded-full grid place-items-center shrink-0 mt-0.5 ${f.correct ? "bg-sage/30 text-sage-light" : "bg-red-500/20 text-red-300"}`}>
                    {f.correct ? <Check size={14} /> : <X size={14} />}
                  </span>
                  <div>
                    <div className="text-parchment/90 text-sm mb-1">{f.question}</div>
                    {!f.correct && <div className="text-xs text-gold mb-1">Answer: {f.correct_answer}</div>}
                    {f.note && <div className="text-xs text-text-muted italic">{f.note}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-4">
            {result.passed ? (
              <button onClick={() => navigate(`/certificate/${courseId}`)} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="quiz-to-certificate">
                <Award size={18} /> Receive your seal
              </button>
            ) : (
              <button onClick={retry} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full gold-border text-parchment hover:bg-gold/10 transition-colors" data-testid="quiz-retry">
                <RotateCcw size={18} /> Reflect again
              </button>
            )}
            <button onClick={() => navigate(`/courses/${courseId}`)} className="px-8 py-3.5 rounded-full gold-border text-text-muted hover:text-parchment transition-colors">Return to teachings</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 bg-sanctuary-deep">
      <div className="max-w-2xl mx-auto px-6">
        <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-2 text-text-muted hover:text-gold text-sm mb-6 transition-colors" data-testid="quiz-back">
          <ArrowLeft size={14} /> Back to the degree
        </Link>
        <ChokuRei className="w-12 text-gold/50 mb-6" />
        <div className="eyebrow mb-3">Attunement Ceremony</div>
        <h1 className="font-display text-4xl text-parchment font-light mb-4">A mindful reflection</h1>
        <p className="text-text-muted leading-relaxed mb-12">{quiz.intro}</p>

        <div className="space-y-8">
          {quiz.questions.map((q, qi) => (
            <div key={qi} data-testid={`quiz-question-${qi}`}>
              <div className="font-display text-2xl text-parchment mb-4">{qi + 1}. {q.question}</div>
              <div className="grid gap-3">
                {q.options.map((opt, oi) => (
                  <button
                    key={oi}
                    onClick={() => setAnswers({ ...answers, [qi]: oi })}
                    className={`text-left rounded-xl px-5 py-4 border transition-colors ${answers[qi] === oi ? "bg-gold/15 border-gold/50 text-parchment" : "bg-sanctuary-card gold-border text-text-muted hover:border-gold/30 hover:text-parchment"}`}
                    data-testid={`quiz-${qi}-option-${oi}`}
                  >
                    <span className="font-jp text-gold/60 mr-3">{["一", "二", "三", "四"][oi]}</span>{opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button onClick={submit} disabled={submitting} className="mt-12 w-full py-4 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors disabled:opacity-60" data-testid="submit-quiz-button">
          {submitting ? "Offering your reflection…" : "Complete the ceremony"}
        </button>
      </div>
    </div>
  );
}
